import React, { useState, KeyboardEvent } from 'react'
import {
  Box,
  Chip,
  TextField,
  Typography,
  FormHelperText,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

interface KeywordInputProps {
  keywords: string[]
  onChange: (keywords: string[]) => void
  placeholder?: string
  maxKeywords?: number
  maxLength?: number
  label?: string
  error?: boolean
  helperText?: string
  onSuggestKeywords?: () => void
  showSuggestionButton?: boolean
  suggestionButtonDisabled?: boolean
}

const KeywordInput: React.FC<KeywordInputProps> = ({
  keywords,
  onChange,
  placeholder = 'キーワードを入力してEnterキーで追加',
  maxKeywords = 10,
  maxLength = 100,
  error = false,
  onSuggestKeywords,
  showSuggestionButton = false,
  suggestionButtonDisabled = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [inputError, setInputError] = useState('')

  const validateKeyword = (keyword: string): string | null => {
    const trimmed = keyword.trim()

    if (trimmed.length === 0) {
      return '空のキーワードは追加できません'
    }

    if (trimmed.length > maxLength) {
      return `キーワードは${maxLength}文字以内で入力してください`
    }

    if (keywords.includes(trimmed)) {
      return 'このキーワードは既に追加されています'
    }

    if (keywords.length >= maxKeywords) {
      return `キーワードは最大${maxKeywords}個まで追加できます`
    }

    return null
  }

  const addKeyword = (keyword: string) => {
    const error = validateKeyword(keyword)
    if (error) {
      setInputError(error)
      return
    }

    const trimmed = keyword.trim()
    onChange([...keywords, trimmed])
    setInputValue('')
    setInputError('')
  }

  const removeKeyword = (indexToRemove: number) => {
    const newKeywords = keywords.filter((_, index) => index !== indexToRemove)
    onChange(newKeywords)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // IME 変換中の Enter (変換確定) では追加しない
    if (event.nativeEvent.isComposing) return
    if (event.key === 'Enter') {
      event.preventDefault()
      if (inputValue.trim()) {
        addKeyword(inputValue)
      }
    } else if (event.key === ',' || event.key === '、') {
      event.preventDefault()
      if (inputValue.trim()) {
        addKeyword(inputValue)
      }
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
    if (inputError) {
      setInputError('')
    }
  }

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        error={error || !!inputError}
        helperText={inputError}
        inputProps={{
          maxLength: maxLength,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          mt: 1,
          minHeight: '32px',
        }}
      >
        <Tooltip title="会話の中で全てのキーワードが検知された場合に自動的にテンプレートを入れます">
          <IconButton
            size="small"
            sx={{
              p: 0.5,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            flexGrow: 1,
          }}
        >
          {keywords.map((keyword, index) => (
            <Chip
              key={index}
              label={keyword}
              onDelete={() => removeKeyword(index)}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
              }}
            />
          ))}
          {keywords.length === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontStyle: 'italic' }}
            >
              キーワードが設定されていません
            </Typography>
          )}
        </Box>

        {showSuggestionButton && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={onSuggestKeywords}
            disabled={suggestionButtonDisabled}
            sx={{
              py: 0.5,
              px: 1.5,
              color: 'text.primary',
              borderColor: 'text.secondary',
              flexShrink: 0,
              '&:hover': {
                borderColor: 'text.primary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            AIで提案
          </Button>
        )}
      </Box>

      <FormHelperText>
        {keywords.length}/{maxKeywords} キーワード
      </FormHelperText>
    </Box>
  )
}

export default KeywordInput
